/**
 * Presentation helpers for audit-log rows.
 *
 * `AuditLog.action` has no enum behind it — every backend call site passes a free-form string —
 * so everything here is a keyword match over the shapes the table actually contains:
 * `resource.started`, `admin.resource.deleted`, `PaymentVerified`.
 */

/** Prefix the backend puts on every action taken from the admin console. */
const ADMIN_PREFIX = "admin.";

/**
 * True when the action was taken by an operator from the admin console rather than by the
 * workspace's own user.
 *
 * Worth surfacing on its own badge: the label alone cannot carry it, because the trailing segment
 * of `admin.resource.started` and of `resource.started` is the same word.
 */
export function isAdminAuditAction(action: string): boolean {
  return action.startsWith(ADMIN_PREFIX);
}

/**
 * Humanizes an action for display: `resource.snapshot.created` → "Snapshot Created",
 * `PaymentVerified` → "Payment Verified".
 *
 * Only the `admin.` prefix and the leading entity segment are dropped — keeping the rest is what
 * separates "Snapshot Created" from "Created".
 */
export function formatAuditAction(action: string): string {
  if (!action) return action;

  const withoutPrefix = action.startsWith(ADMIN_PREFIX)
    ? action.slice(ADMIN_PREFIX.length)
    : action;

  const segments = withoutPrefix.split(".");

  // A dotted action always leads with the entity ("resource", "snapshot"), which the Entity column
  // already shows; a single-segment action is a legacy PascalCase name and keeps every word.
  const meaningful = segments.length > 1 ? segments.slice(1) : segments;

  return meaningful
    .join(" ")
    .replace(/_/g, " ")
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

/**
 * What a filter control calls an action: `order.created` → "Order · Created",
 * `admin.balance.adjusted` → "Balance · Adjusted (admin)", `PaymentVerified` → "Payment Verified".
 *
 * The table badge can drop the entity because the Entity Type column sits beside it. A dropdown
 * option or an active-filter chip has no such column, and without the entity `order.created`,
 * `payment.created` and `snapshot.created` all read "Created". `(admin)` spells out the prefix
 * `formatAuditAction` drops, the only difference between `admin.payment.verified` and
 * `payment.verified`.
 */
export function formatAuditActionFilterLabel(action: string): string {
  if (!action) return action;

  const withoutPrefix = action.startsWith(ADMIN_PREFIX)
    ? action.slice(ADMIN_PREFIX.length)
    : action;

  const segments = withoutPrefix.split(".");

  // A legacy PascalCase name has no entity segment, and its label already carries every word.
  const label =
    segments.length > 1
      ? `${segments[0]
          .replace(/_/g, " ")
          .replace(/\b\w/g, (character) => character.toUpperCase())} · ${formatAuditAction(action)}`
      : formatAuditAction(action);

  return isAdminAuditAction(action) ? `${label} (admin)` : label;
}

/**
 * Badge styling for an action. Best-effort keyword match, since there is no enum to switch on.
 */
export function auditActionVariant(
  action: string,
): "default" | "secondary" | "destructive" | "outline" {
  const lower = action.toLowerCase();

  if (
    lower.includes("deleted") ||
    lower.includes("rejected") ||
    lower.includes("failed") ||
    lower.includes("suspended") ||
    lower.includes("refunded") ||
    lower.includes("revoked")
  ) {
    return "destructive";
  }

  if (
    lower.includes("created") ||
    lower.includes("started") ||
    lower.includes("resumed") ||
    lower.includes("verified") ||
    lower.includes("paid")
  ) {
    return "default";
  }

  if (
    lower.includes("updated") ||
    lower.includes("attached") ||
    lower.includes("detached") ||
    lower.includes("rollback") ||
    lower.includes("rolled_back")
  ) {
    return "outline";
  }

  return "secondary";
}

/**
 * Badge styling for a severity. Unlike the action, this one is a closed set the backend writes:
 * info, warning or error.
 */
export function auditSeverityVariant(
  severity: string,
): "default" | "secondary" | "destructive" | "outline" {
  switch (severity.toLowerCase()) {
    case "error":
      return "destructive";
    case "warning":
      return "outline";
    default:
      return "secondary";
  }
}

/**
 * Humanizes a category for display: `resource_management` → "Resource Management".
 */
export function formatAuditCategory(category: string): string {
  if (!category) return category;

  return category
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

/**
 * Parses a JSON column that the API hands over as a string (`metadata`, `stateBefore`,
 * `stateAfter`, `changes`).
 *
 * Returns null for anything with nothing to show — absent, blank, or the empty object the backend
 * defaults `metadata` to — so a caller can hide the section rather than render "{}". Unparseable
 * text comes back as-is: showing the raw column beats swallowing it, and this is an audit trail.
 */
export function parseAuditJson(value: string | null | undefined): unknown | null {
  if (!value) return null;

  const trimmed = value.trim();
  if (trimmed === "" || trimmed === "{}" || trimmed === "null") return null;

  try {
    return JSON.parse(trimmed);
  } catch {
    return trimmed;
  }
}
